import { ReactElement } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ConfirmIcon, RightArrowIcon } from 'src/v3/core/icons'
import {
  CourseV2PhaseKey,
  CourseV2Progress,
  CourseV2WizardPhase,
} from 'src/v3/classroom-v2/types'

let phaseLabel: Record<CourseV2PhaseKey, string> = {
  content: 'Content',
  assessment: 'Assessment',
  survey: 'Survey',
  certificate: 'Certificate',
}

// Which milestone each wizard phase lights up. preQuiz and quiz both sit on
// 'assessment' — from the student's point of view the checkpoint screen and
// the quiz itself are one milestone, not two.
let currentPhaseKey: Record<CourseV2WizardPhase['kind'], CourseV2PhaseKey> = {
  content: 'content',
  preQuiz: 'assessment',
  quiz: 'assessment',
  survey: 'survey',
  certificate: 'certificate',
}

type PhaseStatus = 'complete' | 'current' | 'upcoming'

// A course with no survey requirement omits the Survey milestone entirely
// rather than rendering a step that will never happen — showing a permanently
// dead milestone would imply the user is missing something they aren't.
let visiblePhaseKeys = (progress: CourseV2Progress): CourseV2PhaseKey[] =>
  progress.survey.kind === 'notRequired'
    ? ['content', 'assessment', 'certificate']
    : ['content', 'assessment', 'survey', 'certificate']

// Completion is read from the progress record, not from position relative to
// the current phase: a user reviewing content after finishing it is on the
// 'content' milestone while Content is legitimately complete, so ordering
// alone would report the wrong thing.
//
// The certificate milestone tracks whether the API has published the
// certificate, which is deliberately a separate question from whether the user
// earned it — they earn it by reaching this phase at all, so a certificate that
// is merely pending reads as 'current' (being issued), not as a failure. Only
// 'issued' completes the track, since that's the only state where the learner
// actually has the thing.
let phaseStatus = (
  key: CourseV2PhaseKey,
  progress: CourseV2Progress,
  current: CourseV2PhaseKey,
): PhaseStatus => {
  let isComplete =
    key === 'content'
      ? progress.allStepsCompleted
      : key === 'assessment'
        ? progress.quiz.kind === 'passed'
        : key === 'survey'
          ? progress.survey.kind === 'submitted'
          : progress.certificate.kind === 'issued'
  if (isComplete) return 'complete'
  return key === current ? 'current' : 'upcoming'
}

let PhaseNode = ({
  label,
  status,
}: {
  label: string
  status: PhaseStatus
}): ReactElement => {
  let theme = useTheme()
  let color =
    status === 'complete'
      ? theme.palette.success.main
      : status === 'current'
        ? theme.palette.primary.main
        : theme.palette.text.disabled

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {status === 'complete' ? (
        <ConfirmIcon sx={{ fontSize: 22, color }} />
      ) : (
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: color,
            // The current phase reads as a filled ring; upcoming phases are
            // flat grey dots. Both are 20px so the track doesn't jitter as
            // the user advances.
            boxShadow:
              status === 'current'
                ? `0 0 0 4px ${theme.palette.primary.main}33`
                : 'none',
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          color,
          fontWeight:
            status === 'upcoming' ? 'fontWeightRegular' : 'fontWeightBold',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

// The horizontal milestone track from the design: Content → Assessment →
// Survey → Certificate. Purely a readout of CourseV2Progress — nothing here
// is clickable, because navigating between phases is never a user choice.
// Which phase renders is derived by selectWizardPhase from the server's
// progress record; letting someone click "Certificate" would be exactly the
// bypass this wizard is built to prevent.
export let CoursePhaseStepper = ({
  progress,
  phase,
}: {
  progress: CourseV2Progress
  phase: CourseV2WizardPhase
}): ReactElement => {
  let current = currentPhaseKey[phase.kind]
  let keys = visiblePhaseKeys(progress)

  return (
    <Box
      component="ol"
      aria-label="Course completion milestones"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 2,
        listStyle: 'none',
        m: 0,
        mb: 5,
        p: 0,
      }}
    >
      {keys.map((key, index) => (
        <Box
          key={key}
          component="li"
          aria-current={key === current ? 'step' : undefined}
          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <PhaseNode
            label={phaseLabel[key]}
            status={phaseStatus(key, progress, current)}
          />
          {index < keys.length - 1 && (
            <RightArrowIcon
              aria-hidden
              sx={{ fontSize: 18, color: 'text.disabled', mb: 3 }}
            />
          )}
        </Box>
      ))}
    </Box>
  )
}
